import React, { useCallback, useContext, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Box } from "../../../../design-system";
import { userContext } from "../../../../../stores/users/userContext";
import { ROUTES } from "../../../../../constants";
import TopBarAvatar from "./TopBarAvatar";
import AccountDropdown from "../../../../account/AccountDropdown";
import AccountSettingsModal from "../../../../account/AccountSettingsModal";

const TOP_BAR_HEIGHT = 70;
const SIDEBAR_WIDTH = 260;
const TOP_BAR_PADDING = "16px 8px";
const TOP_BAR_GAP = "15px";

interface TopBarProps {
  /** Right offset in px (margin from viewport right edge) */
  rightOffset?: number;
  /** Left offset in px on desktop. Defaults to the sidebar width; pass 0 for the
   *  no-sidebar home-page layout variants. */
  leftOffset?: number;
}

const TopBar = ({ rightOffset = 0, leftOffset = SIDEBAR_WIDTH }: TopBarProps) => {
  const userStore = useContext(userContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  if (!userStore.isUserAuth()) return null;

  const handleAvatarClick = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const handleAccountSettings = () => {
    setIsDropdownOpen(false);
    setIsModalOpen(true);
  };

  const handleLogout = () => {
    setIsDropdownOpen(false);
    userStore.logoutBackEnd();
    navigate(ROUTES.LANDING);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        left: { xs: 0, md: `${leftOffset}px` },
        top: 0,
        right: `${rightOffset}px`,
        height: `${TOP_BAR_HEIGHT}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "end",
        padding: TOP_BAR_PADDING,
        gap: TOP_BAR_GAP,
        zIndex: 10,
        // Soft top-to-bottom scrim (no hard "black box" edge) — fades to transparent so
        // content scrolling up reads cleanly behind the Upload button + avatar instead of
        // overlapping them, without looking like a solid bar.
        background:
          "linear-gradient(to bottom, rgba(10,10,14,0.92) 0%, rgba(10,10,14,0.6) 55%, rgba(10,10,14,0) 100%)",
        pointerEvents: "none",
        "& > *": { pointerEvents: "auto" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: TOP_BAR_GAP,
          position: "relative",
        }}
      >
        {userStore.isUserStripeActive() && (
          <Box
            component="button"
            onClick={() => {
              if (location.pathname === "/") {
                window.dispatchEvent(new CustomEvent("upload-open"));
              } else {
                navigate("/?open=1");
              }
            }}
            onMouseEnter={() => window.dispatchEvent(new CustomEvent("upload-hover", { detail: true }))}
            onMouseLeave={() => window.dispatchEvent(new CustomEvent("upload-hover", { detail: false }))}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              background: "#e61d50",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "8px 14px",
              fontFamily: "Outfit, sans-serif",
              fontSize: "14px",
              fontWeight: 700,
              cursor: "pointer",
              "&:hover": { background: "#c0174a" },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 18 }} />
            Upload
          </Box>
        )}
        <TopBarAvatar onClick={handleAvatarClick} isActive={isDropdownOpen} />

        <AccountDropdown
          isOpen={isDropdownOpen}
          username={userStore.getUserFullName() || ""}
          planLabel={userStore.getUserPlan() || "FREE"}
          onAccountSettings={handleAccountSettings}
          onLogout={handleLogout}
          onClose={handleDropdownClose}
        />
      </Box>

      <AccountSettingsModal open={isModalOpen} onClose={handleModalClose} />
    </Box>
  );
};

export default observer(TopBar);
